import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import useClassesStore from '../../../stateManagers/store/useClassesStore';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface ClassSectionFilterProps {
  selectedClass: string | null;
  selectedSection: string | null;
  onClassChange: (classId: string | null) => void;
  onSectionChange: (section: string | null) => void;
  onClassesLoaded?: (classes: ClassData[]) => void;
  // Optional: show "Show All Students" button when class has sections but none selected
  allowAllSections?: boolean;
  showingAllSections?: boolean;
  onShowAllSections?: () => void;
}

const ClassSectionFilter: React.FC<ClassSectionFilterProps> = ({
  selectedClass,
  selectedSection,
  onClassChange,
  onSectionChange,
  onClassesLoaded,
  allowAllSections = false,
  showingAllSections = false,
  onShowAllSections,
}) => {
  const toast = useRef<Toast>(null);
  const { classOptions, fetchClasses, isFetching, hasFetched } = useClassesStore();
  const [hasNotifiedParent, setHasNotifiedParent] = useState(false);

  useEffect(() => {
    if (!hasFetched && !isFetching) {
      fetchClasses(false);
    }
  }, [hasFetched, isFetching, fetchClasses]);

  useEffect(() => {
    if (onClassesLoaded && classOptions.length > 0 && !hasNotifiedParent && hasFetched) {
      const classes: ClassData[] = classOptions.map(opt => ({
        id: opt.id,
        class_name: opt.label,
        sections: opt.sections || []
      }));
      onClassesLoaded(classes);
      setHasNotifiedParent(true);
    }
  }, [classOptions, onClassesLoaded, hasNotifiedParent, hasFetched]);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classOptions.find(c => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const handleClassChange = (classId: string) => {
    if (selectedClass === classId) {
      onClassChange(null);
      onSectionChange(null);
    } else {
      onClassChange(classId);
      onSectionChange(null);
    }
  };

  const handleSectionChange = (section: string) => {
    if (selectedSection === section) {
      onSectionChange(null);
    } else {
      onSectionChange(section);
    }
  };

  const hasSections = selectedClassHasSections();
  const selectedClassData = classOptions.find(c => c.id === selectedClass);
  const sections = selectedClassData?.sections || [];

  // Show the "Show All Students" button when:
  // - allowAllSections is enabled
  // - a class is selected
  // - that class has sections
  // - no section is currently selected
  const showAllSectionsButton =
    allowAllSections &&
    selectedClass !== null &&
    hasSections &&
    selectedSection === null;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .chip-button {
          transition: all 0.2s ease;
        }
        .chip-button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        .chip-button:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.12)',
      }}>
        {/* Classes Section */}
        <div style={{ marginBottom: hasSections ? '16px' : '0' }}>
          <label style={{
            display: 'block',
            fontSize: '11.5px',
            fontWeight: '700',
            color: '#1e40af',
            marginBottom: '10px',
            letterSpacing: '0.02em'
          }}>
            Select Class
          </label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {isFetching ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                Loading classes...
              </div>
            ) : classOptions.length === 0 ? (
              <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                No classes available
              </div>
            ) : (
              classOptions.map((cls) => (
                <button
                  key={cls.id}
                  onClick={() => handleClassChange(cls.id)}
                  disabled={isFetching}
                  className="chip-button"
                  style={{
                    padding: '6px 16px',
                    border: selectedClass === cls.id ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    background: selectedClass === cls.id
                      ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                      : 'white',
                    color: selectedClass === cls.id ? 'white' : '#1f2937',
                    fontSize: '11.5px',
                    fontWeight: '600',
                    cursor: isFetching ? 'not-allowed' : 'pointer',
                    outline: 'none',
                    boxShadow: selectedClass === cls.id ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                    opacity: isFetching ? 0.5 : 1,
                  }}
                >
                  {cls.label}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Sections + optional Show All button */}
        {hasSections && (
          <div style={{ paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
            <label style={{
              display: 'block',
              fontSize: '11.5px',
              fontWeight: '700',
              color: '#1e40af',
              marginBottom: '10px',
              letterSpacing: '0.02em'
            }}>
              Select Section
            </label>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {sections.map((section) => (
                <button
                  key={section}
                  onClick={() => handleSectionChange(section)}
                  className="chip-button"
                  style={{
                    padding: '8px 16px',
                    border: selectedSection === section ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    background: selectedSection === section
                      ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                      : 'white',
                    color: selectedSection === section ? 'white' : '#1f2937',
                    fontSize: '11.5px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: selectedSection === section ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                  }}
                >
                  Section {section}
                </button>
              ))}

              {/* Show All Students button — only rendered when allowAllSections is true */}
              {showAllSectionsButton && (
                <button
                  onClick={onShowAllSections}
                  className="chip-button"
                  style={{
                    padding: '8px 16px',
                    border: showingAllSections ? '1.5px solid #10b981' : '1.5px dashed #10b981',
                    borderRadius: '8px',
                    background: showingAllSections
                      ? 'linear-gradient(135deg, #065f46 0%, #10b981 50%, #34d399 100%)'
                      : 'white',
                    color: showingAllSections ? 'white' : '#065f46',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: showingAllSections ? '0 2px 8px rgba(16, 185, 129, 0.25)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <i className="pi pi-users" style={{ fontSize: '11.5px' }} />
                  All Sections
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClassSectionFilter;
